<template>
    <div>
        <div :id="labelId" class="font-bold text-xs text-slate-700 mb-2">{{ props.label }}</div>
        <vx-box color="info" class="p-4">
            <div role="group" :aria-labelledby="labelId" class="flex flex-col gap-3" data-test="checkbox-group">
                <div
                    v-for="option in props.options"
                    :key="option.value"
                    class="flex gap-2 items-center text-sm cursor-pointer"
                    :class="{ 'opacity-70 cursor-not-allowed!': isLastSelected(option.value) }"
                    :data-test="`${props.name}-${option.value}`"
                    @click.prevent="toggle(option.value)"
                    @keydown.space.prevent="toggle(option.value)"
                    @keydown.enter.prevent="toggle(option.value)"
                >
                    <vx-checkbox
                        :modelValue="selected.includes(option.value)"
                        :aria-label="option.label"
                        :aria-disabled="isLastSelected(option.value) || undefined"
                    ></vx-checkbox>
                    {{ option.label }}
                </div>
            </div>
        </vx-box>
    </div>
</template>

<script lang="ts" setup>
import { VxBox, VxCheckbox } from '@voxie/frontend-components';
import { computed, useId } from 'vue';

type FilterValue = string | number;

const props = defineProps<{
    label: string;
    name: string;
    options: { label: string; value: FilterValue }[];
}>();

const model = defineModel<FilterValue[] | undefined>();

const labelId = useId();

// Unfiltered is undefined rather than every value, so the key stays out of the query string.
const selected = computed(() => model.value ?? props.options.map((option) => option.value));

const isLastSelected = (value: FilterValue) => 1 === selected.value.length && selected.value.includes(value);

const toggle = (value: FilterValue) => {
    if (isLastSelected(value)) {
        return;
    }

    const next = props.options
        .map((option) => option.value)
        .filter((option) => (option === value ? !selected.value.includes(option) : selected.value.includes(option)));

    model.value = next.length === props.options.length ? undefined : next;
};
</script>
